<template>
  <div class="w-full flex justify-center items-center p-0 bg-gray-50 mt-3">
    <div class="w-full max-w-full bg-white border border-gray-200 rounded-xl shadow-md overflow-hidden transition-all duration-300">
      
      <div class="relative bg-white border-b border-gray-100 p-3 flex items-center gap-3">
        <div class="p-2.5 bg-[#386f99]/10 text-[#386f99] rounded-lg">
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M21 15V19C21 19.5304 20.7893 20.0391 20.4142 20.4142C20.0391 20.7893 19.5304 21 19 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V15"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
            <path
              d="M7 10L12 5L17 10"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
            <path
              d="M12 5V15"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        </div>
        <h3 class="text-lg font-bold text-gray-800 capitalize">Export {{ tableName }}</h3>
      </div>

      <div class="p-3 space-y-0 flex flex-col xl:flex-row w-full justify-between items-end gap-2">
        
        <div class="space-y-3 flex-1 h-auto w-full">
          <div class="flex items-center gap-2 text-gray-500 font-bold text-xs uppercase tracking-wider">
            <div class="text-[#386f99]">
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <rect
                  x="3"
                  y="4"
                  width="18"
                  height="18"
                  rx="2"
                  ry="2"
                  stroke="currentColor"
                  stroke-width="2"
                />
                <line
                  x1="16"
                  y1="2"
                  x2="16"
                  y2="6"
                  stroke="currentColor"
                  stroke-width="2"
                />
                <line
                  x1="8"
                  y1="2"
                  x2="8"
                  y2="6"
                  stroke="currentColor"
                  stroke-width="2"
                />
                <line
                  x1="3"
                  y1="10"
                  x2="21"
                  y2="10"
                  stroke="currentColor"
                  stroke-width="2"
                />
              </svg>
            </div>
            <h4>Time Period</h4>
          </div>

          <div class="grid grid-cols-1 md:grid-cols-3 gap-2">
            <label
              v-for="option in rangePeriod"
              :key="option.value"
              class="flex items-center gap-3 p-3 bg-gray-50 border border-gray-200 rounded-lg cursor-pointer transition-all duration-200 select-none"
              :class="{
                'bg-[#386f99]/5 border-[#386f99] text-[#386f99] font-medium':
                  selectedPeriod === Number(option.value) && !customDaysInputValid,
                'opacity-50 cursor-not-allowed bg-gray-100': customDaysInputValid,
              }"
            >
              <input
                type="radio"
                :value="Number(option.value)"
                v-model="selectedPeriod"
                :disabled="customDaysInputValid"
                class="sr-only"
              />
              <div 
                class="w-4 h-4 rounded-full border flex items-center justify-center transition-all duration-200"
                :class="selectedPeriod === Number(option.value) && !customDaysInputValid ? 'border-[#386f99] bg-[#386f99]' : 'border-gray-300 bg-white'"
              >
                <div class="w-1.5 h-1.5 rounded-full bg-white" v-show="selectedPeriod === Number(option.value) && !customDaysInputValid"></div>
              </div>
              <span :class="selectedPeriod === Number(option.value) && !customDaysInputValid ? 'text-gray-900' : 'text-gray-700'" class="text-sm">
                {{ option.label }}
              </span>
            </label>
          </div>
        </div>

        <div class="space-y-3 flex-1 w-full">
          <div class="flex items-center gap-2 text-gray-500 font-bold text-xs uppercase tracking-wider">
            <div class="text-[#386f99]">
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M12 20H21"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
                <path
                  d="M16.5 3.5C16.8978 3.10218 17.4374 2.87868 18 2.87868C18.5626 2.87868 19.1022 3.10218 19.5 3.5C19.8978 3.89782 20.1213 4.43739 20.1213 5C20.1213 5.56261 19.8978 6.10218 19.5 6.5L7 19L3 20L4 16L16.5 3.5Z"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            </div>
            <h4>Custom Range</h4>
          </div>

          <div class="space-y-1.5 text-start">
            <div class="relative flex items-center">
              <input
                id="customDays"
                type="text"
                v-model="customDaysInput"
                @input="filterCustomDays"
                placeholder="Enter days (1-60)"
                class="w-full bg-white border border-gray-300 rounded-lg pl-3 pr-10 py-2.5 text-sm text-black placeholder:text-gray-400 outline-none transition-all duration-200 focus:border-[#386f99] focus:ring-1 focus:ring-[#386f99]"
                :class="{
                  '!border-red-500 !ring-red-500 bg-red-50/30': customDaysInput && !customDaysInputValid,
                  '!border-green-500 !ring-green-500 bg-green-50/20': customDaysInputValid,
                }"
              />
              <div class="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
                <svg
                  v-if="customDaysInputValid"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  class="text-green-600"
                >
                  <path
                    d="M20 6L9 17L4 12"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
                <svg
                  v-else-if="customDaysInput && !customDaysInputValid"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  class="text-red-500"
                >
                  <circle
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    stroke-width="2"
                  />
                  <line
                    x1="15"
                    y1="9"
                    x2="9"
                    y2="15"
                    stroke="currentColor"
                    stroke-width="2"
                  />
                  <line
                    x1="9"
                    y1="9"
                    x2="15"
                    y2="15"
                    stroke="currentColor"
                    stroke-width="2"
                  />
                </svg>
              </div>
            </div>
            <small
              v-if="customDaysInput && !customDaysInputValid"
              class="block text-xs font-medium text-red-500 mt-1 pl-1"
            >
              Please enter a number between 1 and 60
            </small>
          </div>
        </div>

        <div class="pt-2">
          <button
            @click="startExport"
            class="w-full relative flex justify-center items-center py-3 px-4 bg-[#386f99] hover:bg-[#2c587a] active:bg-[#234661] text-white font-semibold text-sm rounded-lg shadow-md transition-colors duration-200 select-none
                   disabled:bg-gray-300 disabled:text-gray-500 disabled:cursor-not-allowed disabled:shadow-none"
            :disabled="isLoading || (!selectedPeriod && !customDaysInputValid)"
          >
            <div class="flex items-center gap-2">
              <span v-if="isLoading" class="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
              <span v-else class="flex-shrink-0">
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M21 15V19C21 19.5304 20.7893 20.0391 20.4142 20.4142C20.0391 20.7893 19.5304 21 19 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V15"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                  <path
                    d="M7 10L12 5L17 10"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                  <path
                    d="M12 5V15"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
              </span>
              <span>
                {{ isLoading ? "Exporting..." : "Export Data" }}
              </span>
            </div>
          </button>
        </div>

      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import { getDownloadCsv } from "@/api/axiosClient";

export default {
  name: "ExportDataManager",
  props: {
    tableName: {
      type: String,
      required: true,
    },
    skinFilt: {
      type: Number,
      required: true,
    },
    rangePeriod: {
      type: Array,
      default: () => [
        { label: "Today", value: 1 },
        { label: "Last 7 Days", value: 7 },
        { label: "Last 2 Weeks", value: 14 },
      ],
    },
  },
  data() {
    return {
      selectedPeriod: 1,
      customDaysInput: "",
      customDays: null,
      isLoading: false,
    };
  },
  watch: {
    selectedPeriod(newVal) {
      if (newVal !== null) {
        this.customDaysInput = "";
        this.customDays = null;
      }
    },
    customDaysInput(newVal) {
      if (!newVal) {
        this.selectedPeriod = 1;
      }
    },
  },
  computed: {
    ...mapGetters(["isMenuOpen"]),
    customDaysInputValid() {
      return (
        this.customDays !== null &&
        this.customDays >= 1 &&
        this.customDays <= 60
      );
    },
  },
  methods: {
    filterCustomDays(event) {
      let digitsOnly = event.target.value.replace(/\D/g, "");
      let intValue = parseInt(digitsOnly, 10);

      if (!isNaN(intValue)) {
        if (intValue > 60) {
          intValue = 60;
          digitsOnly = "60";
        } else if (intValue < 1) {
          intValue = null;
          digitsOnly = "";
        }
      } else {
        intValue = null;
        digitsOnly = "";
      }

      this.customDaysInput = digitsOnly;
      this.customDays = intValue;

      if (intValue !== null) {
        this.selectedPeriod = null;
      }
    },

    startExport() {
      const today = new Date();
      const daysBack =
        this.customDays !== null ? this.customDays : this.selectedPeriod;

      const start = new Date(today);
      start.setDate(start.getDate() - daysBack + 1);

      const formatDate = (date) => date.toISOString().split("T")[0];
      const start_date = formatDate(start);
      const end_date = formatDate(today);

      const params = { start_date, end_date };
      const url = this.tableName;

      this.isLoading = true;

      getDownloadCsv(url, params, "")
        .then((data) => {
          const blob = new Blob([data], {
            type: "text/csv;charset=utf-8;",
          });

          const link = document.createElement("a");
          const urlBlob = window.URL.createObjectURL(blob);
          link.href = urlBlob;
          link.setAttribute("download", `${this.tableName}-${end_date}.csv`);
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(urlBlob);
        })
        .catch((error) => {
          console.error("Error downloading CSV:", error);
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
  },
};
</script>